import { accumulateOrders } from './orders-aggregator.utils';
import { addPropOperator } from '../../helpers';
import { BLOTTER_ADAPTER_SERVICE, IBlotterAdapter } from '../message-adapters/blotter-adapter';
import { finalize, scan, shareReplay, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { FlushableOrdersTransaction, IOrdersAggregatorService } from './orders-aggregator.interface';
import { IBlotterUpdate, IOrderReport, ORDER_KEY } from '../../interfaces';
import { inject, injectable } from 'inversify';
import { ISelectedClientService, SELECTED_CLIENT_SERVICE } from '../selected-client';
import { ISessionLifecycle, SESSION_LIFECYCLE_SERVICE } from '../session-lifecycle';
import { Observable } from 'rxjs';

@injectable()
export class OrdersAggregatorService implements IOrdersAggregatorService {

    @inject(BLOTTER_ADAPTER_SERVICE) private readonly blotterAdapterService: IBlotterAdapter;

    @inject(SELECTED_CLIENT_SERVICE) private readonly selectedClientSrv: ISelectedClientService;

    @inject(SESSION_LIFECYCLE_SERVICE) private readonly sessionLifeCycle: ISessionLifecycle;

    private ordersPoolObs$: Observable<IBlotterUpdate>;

    ordersObservable = (): Observable<IBlotterUpdate> =>
        this.aggregatedOrders().pipe(
            takeUntil(this.sessionLifeCycle.destroyObservable()),
        );

    private readonly aggregatedOrders = (): Observable<IBlotterUpdate> =>
        this.ordersPoolObs$ ?? (this.ordersPoolObs$ =
            this.rawTransactions().pipe(
                finalize(() => this.ordersPoolObs$ = null),
                scan(
                    accumulateOrders,
                    { allOrders: new Map<IOrderReport[typeof ORDER_KEY], IOrderReport>() } as IBlotterUpdate,
                ),
                shareReplay(1),
            )
        );

    private readonly rawTransactions = (): Observable<FlushableOrdersTransaction> =>
        this.selectedClientSrv.asObservable().pipe(
            takeUntil(this.sessionLifeCycle.destroyObservable()),
            switchMap(client =>
                this.blotterAdapterService.ordersObservable(client?.id).pipe(
                    addPropOperator('flush', false),
                    startWith({ flush: true as const }),
                ),
            ),
        );
}
